import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { IconRegistryService } from '@shared/services/icon-registry.service';
import { CustomIcons } from '@shared/models/custom-icons';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Locale } from '@shared/models/locale';
import { Observable } from 'rxjs';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import { SharedState } from '@shared/state/shared/shared.state';
import { AuthState } from '@auth/store/auth/auth.state';
import { CreateUserApiKey, GetUserApiKey } from '@shared/state/shared/shared.action';
import { Select, Store } from '@ngxs/store';
import { AuthorizationDialogComponent } from '@auth/components/authorization-dialog/authorization-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserInfo } from '@auth/models/auth.state';
import { MirSnackbarType } from '@shared/components/snackbar-message/model/snackbar';
import { SnackBarService } from '@shared/components/snackbar-message/services/snackbar.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { ActionsExecuting, actionsExecuting } from '@ngxs-labs/actions-executing';
import { CardInfo } from '../../models/card';
import { DevelopersRoutes } from '../../models/developers-routes';

@Component({
  selector: 'app-developers-main',
  templateUrl: './developers-main.component.html',
  styleUrls: ['./developers-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DevelopersMainComponent implements OnInit {
  public cardsInfo: CardInfo[] = [
    {
      title: 'DEVELOPER.API_DOCUMENTATION_HEADER',
      text: 'DEVELOPER.API_DOCUMENTATION_TEXT',
      buttonName: 'DEVELOPER.API_DOCUMENTATION_LINK',
      icon: CustomIcons.ApiDoc,
      route: DevelopersRoutes.Documentation
    },
    {
      title: 'DEVELOPER.API_USE_HEADER',
      text: 'DEVELOPER.API_USE_TEXT',
      buttonName: 'DEVELOPER.API_USE_LINK',
      icon: CustomIcons.ApiUse,
      route: DevelopersRoutes.UseApi,
      mobileText: 'DEVELOPER.API_USE_TEXT_MOBILE'
    },
    {
      title: 'DEVELOPER.API_STATISTICS_HEADER',
      text: 'DEVELOPER.API_STATISTICS_TEXT',
      buttonName: 'DEVELOPER.API_STATISTICS_LINK',
      icon: CustomIcons.ApiKey,
      buttonClick: () => this.getApiKey(DevelopersRoutes.Statics)
    }
  ];
  public langChange$: Observable<LangChangeEvent>;
  public icons = CustomIcons;
  public user: UserInfo | null;
  public hideApiKey = true;
  public isRuLocale = true;
  @Select(actionsExecuting([GetUserApiKey, CreateUserApiKey])) public loadingApiKey$: Observable<ActionsExecuting>;
  @Select(SharedState.userDataApiKey) public currentApiKey$: Observable<string | null>;
  public authenticatedUser$: Observable<UserInfo | null>;
  constructor(
    private router: Router,
    private dialog: MatDialog,
    iconService: IconRegistryService,
    private translateService: TranslateService,
    private store: Store,
    private snackBarService: SnackBarService,
    private clipboard: Clipboard
  ) {
    iconService.addIconPack([CustomIcons.Developers]);
  }

  private translate(lang: string): void {
    this.isRuLocale = lang === Locale.RU_RU;
  }

  public ngOnInit(): void {
    this.authenticatedUser$ = this.store.select(AuthState.authenticatedUser).pipe(
      tap(user => {
        this.user = user;
        if (this.user) {
          this.store.dispatch(new GetUserApiKey());
        }
      })
    );

    this.translate(this.translateService.currentLang);
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        this.translate(res.lang);
      })
    );
  }

  public copyLinks(apiKey: string): void {
    this.snackBarService.open(MirSnackbarType.Success, [
      {
        translate: true,
        message: 'SNACK_BAR_MESSAGE.COPY_APIKEY'
      }
    ]);
    this.clipboard.copy(apiKey);
  }

  public getApiKey(route: DevelopersRoutes | null = null): void {
    const user = this.store.selectSnapshot(AuthState.authenticatedUser);
    if (!user) {
      const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
      authorizationDialog.componentInstance.keyOperations = 'GetUserApiKey';
      if (route) {
        authorizationDialog.componentInstance.operationValue = { route: `developers/${route}` };
      }
    } else if (route) {
      this.router.navigate([`developers/${route}`]);
    } else {
      this.store.dispatch(new CreateUserApiKey());
    }
  }

  public goInformationPlacement(): void {
    if (this.user) {
      this.router.navigate([`information-placement`]);
    } else {
      const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
      authorizationDialog.componentInstance.keyOperations = 'InformationPlacement';
      authorizationDialog.componentInstance.operationValue = 'InformationPlacement';
    }
  }
}
